import { loadStripe, Stripe } from "@stripe/stripe-js";

import { supabase } from "@/lib/supabaseClient";

import {
  PlanCode,
  PlanDefinition,
  fetchPlanDefinitions,
} from "@/lib/billing/plans";

export const STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

export const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PLAN_CODES: PlanCode[] = [
  "employer_starter",
  "employer_growth",
  "employer_enterprise",
];

const DEFAULT_PLAN_DEFINITIONS: Record<PlanCode, PlanDefinition> = {
  employer_starter: {
    code: "employer_starter",
    displayName: "Starter",
    monthlyPriceCents: 19900,
    placementFeePercentage: 0.05,
    jobPostingLimit: 2,
    candidateViewLimit: 10,
    sortOrder: 1,
  },
  employer_growth: {
    code: "employer_growth",
    displayName: "Growth",
    monthlyPriceCents: 39900,
    placementFeePercentage: 0.04,
    jobPostingLimit: 5,
    candidateViewLimit: 25,
    sortOrder: 2,
  },
  employer_enterprise: {
    code: "employer_enterprise",
    displayName: "Enterprise",
    monthlyPriceCents: 89900,
    placementFeePercentage: 0.03,
    jobPostingLimit: null,
    candidateViewLimit: null,
    sortOrder: 3,
  },
};

const OVERRIDE_PRICE_IDS: Partial<Record<PlanCode, string>> = {
  employer_starter: import.meta.env.VITE_STRIPE_PRICE_STARTER,
  employer_growth: import.meta.env.VITE_STRIPE_PRICE_GROWTH,
  employer_enterprise: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE,
};

const DEFAULT_PRICE_IDS: Record<PlanCode, string> = {
  employer_starter:
    OVERRIDE_PRICE_IDS.employer_starter ?? "price_employer_starter_monthly",
  employer_growth:
    OVERRIDE_PRICE_IDS.employer_growth ?? "price_employer_growth_monthly",
  employer_enterprise:
    OVERRIDE_PRICE_IDS.employer_enterprise ??
    "price_employer_enterprise_monthly",
};

export interface PlanCatalogEntry {
  code: PlanCode;
  name: string;
  monthlyPriceCents: number;
  placementFeePercentage: number;
  jobPostingLimit: number | null;
  candidateViewLimit: number | null;
  sortOrder: number;
  priceId: string;
}

const planDefinitionCache = new Map<PlanCode, PlanDefinition>();

function getDefinition(code: PlanCode): PlanDefinition {
  return planDefinitionCache.get(code) ?? DEFAULT_PLAN_DEFINITIONS[code];
}

function toCatalogEntry(def: PlanDefinition): PlanCatalogEntry {
  return {
    code: def.code,
    name: def.displayName,
    monthlyPriceCents: def.monthlyPriceCents,
    placementFeePercentage: def.placementFeePercentage,
    jobPostingLimit:
      def.jobPostingLimit === undefined ? null : def.jobPostingLimit,
    candidateViewLimit:
      def.candidateViewLimit === undefined ? null : def.candidateViewLimit,
    sortOrder: def.sortOrder,
    priceId: DEFAULT_PRICE_IDS[def.code],
  };
}

export function isPlanCode(value: string): value is PlanCode {
  return PLAN_CODES.includes(value as PlanCode);
}

export function getPlanByCode(
  planType: string,
): PlanCatalogEntry | undefined {
  if (!isPlanCode(planType)) return undefined;
  return toCatalogEntry(getDefinition(planType));
}

export function getPlanCatalog(): PlanCatalogEntry[] {
  return PLAN_CODES.map((code) => toCatalogEntry(getDefinition(code))).sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
}

export async function refreshPlanCatalog(): Promise<PlanCatalogEntry[]> {
  const definitions = await fetchPlanDefinitions();
  definitions.forEach((def) => {
    if (PLAN_CODES.includes(def.code)) {
      planDefinitionCache.set(def.code, def);
    }
  });

  return getPlanCatalog();
}

export function getPlanDisplayName(planType: string): string {
  return getPlanByCode(planType)?.name ?? planType;
}

export function getPlanLimits(planType: string): {
  jobPostingLimit: number | null;
  candidateViewLimit: number | null;
} | null {
  const plan = getPlanByCode(planType);
  if (!plan) return null;

  return {
    jobPostingLimit: plan.jobPostingLimit,
    candidateViewLimit: plan.candidateViewLimit,
  };
}

export function getPlacementFeePercentage(planType: string): number | null {
  const plan = getPlanByCode(planType);
  return plan ? plan.placementFeePercentage : null;
}

export type SubscriptionStatus =
  | "active"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "past_due"
  | "trialing"
  | "unpaid";

export type CustomerType = "employer" | "candidate";

export type PaymentIntentType = "subscription" | "one_time" | "placement_fee";

export class StripeService {
  private static instance: StripeService;
  private stripe: Stripe | null = null;

  private constructor() {}

  static getInstance(): StripeService {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService();
    }
    return StripeService.instance;
  }

  async initialize(): Promise<void> {
    if (!this.stripe) {
      this.stripe = await stripePromise;
    }
  }

  async getClient(): Promise<Stripe | null> {
    if (!this.stripe) {
      await this.initialize();
    }
    return this.stripe;
  }

  async createCheckoutSession(params: {
    priceId: string;
    customerId?: string;
    customerEmail?: string;
    successUrl: string;
    cancelUrl: string;
    metadata?: Record<string, string>;
  }) {
    if (!this.stripe) {
      throw new Error("Stripe not initialized");
    }

    const response = await fetch("/api/stripe/create-checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    const session = await response.json();
    return session;
  }

  async createPlacementFeePayment(params: {
    employerId: string;
    candidateId: string;
    jobId: string;
    salaryCents: number;
    candidateName?: string;
    positionTitle?: string;
    startDate?: string;
  }) {
    const { data, error } = await supabase.functions.invoke(
      "create-placement-fee-payment",
      {
        body: params,
      },
    );

    if (error) {
      throw new Error(error.message ?? "Unable to create placement fee payment");
    }

    return data;
  }

  async createCustomerPortalSession(customerId: string, returnUrl: string) {
    const response = await fetch("/api/stripe/create-portal-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ customerId, returnUrl }),
    });

    const session = await response.json();
    return session;
  }

  async getSubscription(subscriptionId: string) {
    const response = await fetch(`/api/stripe/subscriptions/${subscriptionId}`);
    return response.json();
  }

  async cancelSubscription(subscriptionId: string) {
    const response = await fetch(
      `/api/stripe/subscriptions/${subscriptionId}/cancel`,
      {
        method: "POST",
      },
    );
    return response.json();
  }

  async updateSubscription(subscriptionId: string, priceId: string) {
    const response = await fetch(`/api/stripe/subscriptions/${subscriptionId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ priceId }),
    });
    return response.json();
  }
}

export const useStripeService = () => {
  return StripeService.getInstance();
};

export const validatePaymentAmount = (amount: number): boolean => {
  return amount > 0 && amount <= 100000;
};

export const formatCurrency = (
  amount: number,
  currency: string = "USD",
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount / 100);
};

export const calculateTax = (amount: number, taxRate: number = 0.08): number =>
  Math.round(amount * taxRate);

export const calculateTotal = (
  amount: number,
  taxRate: number = 0.08,
): number => amount + calculateTax(amount, taxRate);

export const calculatePlacementFee = (
  salary: number,
  planType: string,
): number => {
  const percentage = getPlacementFeePercentage(planType);
  if (percentage === null) return 0;
  return Math.round(salary * percentage);
};

export const trackCandidateView = (
  planType: string,
  currentViews: number,
): boolean => {
  const plan = getPlanByCode(planType);
  if (!plan) return false;

  const limit = plan.candidateViewLimit;
  if (limit === null || limit < 0) return true;

  return currentViews < limit;
};

export const trackJobPosting = (
  planType: string,
  currentPostings: number,
): boolean => {
  const plan = getPlanByCode(planType);
  if (!plan) return false;

  const limit = plan.jobPostingLimit;
  if (limit === null || limit < 0) return true;

  return currentPostings < limit;
};
